import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FirestoreService } from "../../../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../../api/firebase/references";
import { usePaRequestsActionFieldOptions } from "../../../../components/modals/shared/usePaRequestsActionFieldOptions";
import { useCmmInputUniqueValuesContext } from "../../../../context/cmmInputUniqueValuesContext";
import { mergePaRequestsMetadataIntoConditionConfig } from "../../../../data-model/pharmacyOrderUpdatesPaRequestsMetadata";
import type {
  PocReassignmentDoc,
  PocReassignmentEditContext,
  PocReassignmentRuleFormData,
  PocReassignmentRuleState,
} from "../../../../data-model/pocReassignmentRule";
import type { QueryBuilderValue } from "../../../../data-model/queryBuilder";
import { ModalId } from "../../../../enums/modalId";
import { useModalOpener } from "../../../../hooks/useModalOpener";
import {
  closeModal,
  setOpenedModalId,
} from "../../../../redux/slice/modalSliceNew";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import {
  buildPocReassignmentConditionConfigFromCmmColumns,
  createEmptyPocReassignmentRule,
  DEFAULT_POC_REASSIGNMENT_DOC_ID,
  getDefaultPocReassignmentRules,
  getInitialFormDataForPocReassignmentModal,
  mapPocReassignmentDocToRuleStates,
  mapRuleStatesToFirestorePayload,
  POC_REASSIGNMENT_CONDITION_FIELD_OPTIONS,
  POC_REASSIGNMENT_CONDITION_TITLE,
} from "./pocReassignmentUtils";

const cloneRules = (
  rules: PocReassignmentRuleState[],
): PocReassignmentRuleState[] =>
  JSON.parse(JSON.stringify(rules)) as PocReassignmentRuleState[];

export function usePocReassignmentRules(
  firestoreDoc: PocReassignmentDoc | null,
) {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const appliedDocRef = useRef(false);
  const [rules, setRules] = useState<PocReassignmentRuleState[]>(
    getDefaultPocReassignmentRules(),
  );
  const [editContext, setEditContext] =
    useState<PocReassignmentEditContext>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const lastSavedRulesRef = useRef<PocReassignmentRuleState[]>(
    getDefaultPocReassignmentRules(),
  );
  const [lastSavedMeta, setLastSavedMeta] = useState<{
    updated_at?: string;
    updated_by?: string;
  } | null>(null);
  /** Incremented when doc is loaded from Firestore so QueryBuilder remounts with saved values. */
  const [ruleLoadedVersion, setRuleLoadedVersion] = useState(0);
  /** Cached original doc — preserves created_at/created_by on subsequent saves. */
  const existingDocRef = useRef<PocReassignmentDoc | null>(null);

  useEffect(() => {
    if (firestoreDoc == null || appliedDocRef.current) return;
    appliedDocRef.current = true;
    existingDocRef.current = firestoreDoc;
    const mapped = mapPocReassignmentDocToRuleStates(firestoreDoc);
    const seed = mapped.length > 0 ? mapped : getDefaultPocReassignmentRules();
    setRules(seed);
    lastSavedRulesRef.current = cloneRules(seed);
    setLastSavedMeta({
      updated_at: firestoreDoc.updated_at,
      updated_by: firestoreDoc.updated_by,
    });
    setRuleLoadedVersion((v) => v + 1);
  }, [firestoreDoc]);

  const markDirty = () => setIsDirty(true);

  useModalOpener();

  const handleAddNewRuleClick = () => {
    markDirty();
    setRules((prev) => [...prev, createEmptyPocReassignmentRule(prev.length)]);
  };

  const openModalForEditRule = (ruleId: string) => {
    setEditContext({ ruleId });
    dispatch(setOpenedModalId({ id: ModalId.ADD_NEW_POC_REASSIGNMENT_MODAL }));
  };

  const handleCloseModal = () => {
    setEditContext(null);
    dispatch(closeModal());
  };

  const handleRuleConditionChange = (
    ruleId: string,
    newCondition: QueryBuilderValue,
  ) => {
    markDirty();
    setRules((prev) =>
      prev.map((r) =>
        r.id === ruleId ? { ...r, condition: newCondition } : r,
      ),
    );
  };

  const handleUpdateRule = (
    ruleId: string,
    formData: PocReassignmentRuleFormData,
  ) => {
    markDirty();
    setRules((prev) =>
      prev.map((r) =>
        r.id !== ruleId
          ? r
          : {
              ...r,
              label: formData.label,
              subtext: formData.subtext,
              active: formData.active,
              lookback: formData.lookback,
              actions: formData.actions.map((a) => ({
                ...a,
                value: Array.isArray(a.value) ? [...a.value] : a.value,
              })),
              triggers: formData.triggers.map((t) => ({ ...t })),
            },
      ),
    );
  };

  const handleDeleteRule = (ruleId: string) => {
    markDirty();
    setRules((prev) => prev.filter((r) => r.id !== ruleId));
  };

  const initialFormDataForModal = getInitialFormDataForPocReassignmentModal(
    editContext,
    rules,
  );

  const { cmmInputUniqueValues } = useCmmInputUniqueValuesContext();
  const { metadata: paRequestsMetadata, fieldOptions: paRequestsFieldOptions } =
    usePaRequestsActionFieldOptions();

  const conditionConfig = useMemo(() => {
    const pageConfig = buildPocReassignmentConditionConfigFromCmmColumns(
      cmmInputUniqueValues,
      POC_REASSIGNMENT_CONDITION_FIELD_OPTIONS,
    );
    const mergedFromMetadata = mergePaRequestsMetadataIntoConditionConfig(
      pageConfig,
      paRequestsMetadata,
      POC_REASSIGNMENT_CONDITION_FIELD_OPTIONS,
    );
    return mergedFromMetadata ?? pageConfig;
  }, [cmmInputUniqueValues, paRequestsMetadata]);

  const actionFieldOptions = useMemo(
    () =>
      paRequestsFieldOptions.length > 0
        ? paRequestsFieldOptions
        : conditionConfig.fields.map((f) => ({
            value: f.value,
            label: f.label,
          })),
    [paRequestsFieldOptions, conditionConfig],
  );

  const configKey = useMemo(
    () =>
      conditionConfig.fields.length > 0
        ? conditionConfig.fields
            .map((f) => f.value)
            .sort()
            .join(",")
        : "empty",
    [conditionConfig],
  );

  const handleModalSave = async (data: PocReassignmentRuleFormData) => {
    if (editContext == null) return;
    handleUpdateRule(editContext.ruleId, data);
    toast.success("Rule updated");
    handleCloseModal();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = mapRuleStatesToFirestorePayload(
        rules,
        existingDocRef.current,
        user?.email ?? undefined,
      );
      /** setDocument (vs. updateDocument) handles the first-save case where the Firestore doc does not yet exist. */
      await FirestoreService.setDocument(
        FirestoreCollectionReference.pharmacyOrderUpdatesRules(),
        DEFAULT_POC_REASSIGNMENT_DOC_ID,
        payload,
      );
      existingDocRef.current = {
        ...(existingDocRef.current ?? {
          id: DEFAULT_POC_REASSIGNMENT_DOC_ID,
          rules: [],
        }),
        ...payload,
      };
      lastSavedRulesRef.current = cloneRules(rules);
      setIsDirty(false);
      setLastSavedMeta({
        updated_at: payload.updated_at,
        updated_by: payload.updated_by ?? user?.email ?? undefined,
      });
      toast.success("Changes saved");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (!window.confirm("Are you sure? All unsaved changes will be lost."))
      return;
    setRules(cloneRules(lastSavedRulesRef.current));
    setIsDirty(false);
  };

  return {
    rules,
    editContext,
    conditionConfig,
    actionFieldOptions,
    configKey,
    conditionTitle: POC_REASSIGNMENT_CONDITION_TITLE,
    isDirty,
    saving,
    ruleLoadedVersion,
    handleAddNewRuleClick,
    openModalForEditRule,
    handleCloseModal,
    handleRuleConditionChange,
    handleDeleteRule,
    initialFormDataForModal,
    handleModalSave,
    handleSave,
    handleDiscard,
    lastSavedMeta,
  };
}
